import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FocusKeyManager } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  forwardRef,
  ChangeDetectorRef,
  SimpleChanges,
  OnDestroy,
  OnChanges,
  QueryList,
  ContentChildren,
  Input,
  Output,
  EventEmitter,
  Optional,
  SkipSelf,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { PhxRadioListService } from './phx-radio-list.service';
import { PhxRadioItemComponent } from './phx-radio.component';

import { KeyboardKeys } from '../../helpers/KeyboardKeys.enum';
import mixinDisabled from '../../mixins/disabled';
import { PhxContextService, PHOENIX_CONTEXTS } from '../../services/phx-context-service';
import { PhxRadioListBackground, PhxRadioListSize, PhxRadioListAlignment, PhxRadioValue } from './phx-radio-list.types';
import { PHX_SIZE, PHX_BACKGROUND } from '../../constants';

const baseRadioListMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-radio-list',
  styleUrls: ['./phx-radio-list.scss'],
  template: `<ng-content></ng-content>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['phxDisabled'],
  providers: [
    PhxRadioListService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxRadioListComponent),
      multi: true,
    },
  ],
  host: {
    class: 'phx-radio-list',
    '[class.phx-radio]': 'isRadio',
    '[class.phx-radio-button]': 'isRadioButton',
    '[class.phx-radio-tile]': 'isRadioTile',
    '[class.phx-radio-list-align-horizontal]': 'phxAlignment === "horizontal"',
    '[class.phx-filter]': 'phxStyleType === PHOENIX_CONTEXTS.FILTER',
    '(keydown)': 'handleKeyboard($event)',
    '[attr.tabindex]': 'phxDisabled ? null : 0',
    '[attr.phx-non-default-selected]': 'isValueSelected && !isDefaultSelection'
  },
})
export class PhxRadioListComponent extends baseRadioListMixin implements ControlValueAccessor, OnDestroy, OnChanges {
  /**
   * Changes the size of phx-radio-button. Only applicable for phx-radio-button variation.
   */
  @Input() public phxSize: PhxRadioListSize = PHX_SIZE.MD;

  /**
   * Changes the color of phx-radio-button based on this background. Only applicable for phx-radio-button variation.
   */
  @Input() public phxBackground: PhxRadioListBackground = PHX_BACKGROUND.STANDARD;

  /**
   * To change the alignment of the radio list, either horizontally or vertically.
   * Not applicable for phx-radio-button variation.
   */
  @Input() public phxAlignment: PhxRadioListAlignment = 'vertical';

  /**
   * Use to pass down the default value for the radio list.
   * This is used to track filter count in the filter row.
   * The value passed as phxDefaultValue will be selected by default if no value is provided.
   */
  @Input() public phxDefaultValue: PhxRadioValue | null = null;

  /**
   * Event that emits only when the user changes the selection from within the component,
   * not when the value is set externally (e.g., through form control writeValue)
   */
  @Output() public readonly phxRadioSelectionChanged = new EventEmitter<PhxRadioValue>();

  @ContentChildren(PhxRadioItemComponent, { descendants: true }) phxRadioItems: QueryList<PhxRadioItemComponent>;

  public isRadio = false;
  public isRadioButton = false;
  public isRadioTile = false;
  public readonly PHOENIX_CONTEXTS = PHOENIX_CONTEXTS;
  public phxStyleType: PHOENIX_CONTEXTS;

  private _destroy$: Subject<void> = new Subject<void>();
  private _value: PhxRadioValue = null;
  private _keyManager: FocusKeyManager<PhxRadioItemComponent>;
  private _onChange = (value: any) => {};
  private _onTouched = () => {};

  constructor(private _cdr: ChangeDetectorRef, private _phxRadioListService: PhxRadioListService, @Optional() @SkipSelf() private _contextService: PhxContextService) {
    super();
    this.phxStyleType = this._contextService?.getType();
    this._initContainerContext();
  }

  ngAfterViewInit(): void {
    this._keyManager = new FocusKeyManager(this.phxRadioItems).skipPredicate((item) => !!item?.phxDisabled).withWrap();
  }

  ngAfterContentInit(): void {
    this.isRadio = this.phxRadioItems.first?.isRadio;
    this.isRadioButton = this.phxRadioItems.first?.isRadioButton;
    this.isRadioTile = this.phxRadioItems.first?.isRadioTile;

    this.phxRadioItems.forEach((item, index) => {
      item.phxRadioItemClicked.pipe(takeUntil(this._destroy$)).subscribe(() => {
        this.handleFocus(index);
      });
    });
  }

  ngOnInit(): void {
    this._phxRadioListService.selected$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (this._value !== value) {
        this._value = value;
        this._onChange(this._value);
      }
    });

    // Listen for internal selections to emit phxRadioSelectionChanged
    this._phxRadioListService.internalSelection$.pipe(takeUntil(this._destroy$)).subscribe((value) => {
      if (value !== null) {
        this.phxRadioSelectionChanged.emit(value);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxDisabled, phxBackground, phxSize } = changes;

    if (phxDisabled) this._phxRadioListService.setDisabled(this.phxDisabled);
    if (phxBackground) this._phxRadioListService.setBackground(this.phxBackground || 'standard');
    if (phxSize) this.updateSize(this.phxSize);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private setValue(value : PhxRadioValue): void {
    this._value = value;
    this._phxRadioListService.select(value);
    this._cdr.markForCheck();
  }

  public handleFocus(index: number): void {
    const currentIndex = index === -1 ? 0 : index;
    this._keyManager.setActiveItem(currentIndex);
  }

  public handleKeyboard(event: KeyboardEvent): void {
    switch (event.code) {
      case KeyboardKeys.ARROW_LEFT:
      case KeyboardKeys.ARROW_UP:
        event.preventDefault();
        this._keyManager.setPreviousItemActive();
        break;
      case KeyboardKeys.ARROW_RIGHT:
      case KeyboardKeys.ARROW_DOWN:
        event.preventDefault();
        this._keyManager.setNextItemActive();
        break;
    }
  }

  public get isDefaultSelection(): boolean {
    return this._value === this.phxDefaultValue;
  }

  public get isValueSelected(): boolean {
    return !!this._value;
  }

  public registerOnChange(fn: (value: any) => void) {
    this._onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  public writeValue(value: any): void {
    // If no value is provided and default value is set, use the default value.
    if(!value && this.phxDefaultValue) {
      this.setValue(this.phxDefaultValue);
      return;
    }
    if (this._value !== value) {
      this.setValue(value);
    }
  }

  private updateSize(size: PhxRadioListSize): void {
    this._phxRadioListService.setSize(size);
    this._cdr.markForCheck();
  }

  /** Subscribes to the container context size map if this component is inside a container (e.g. phx-overview).
   * Input size takes priority — if phxSize was explicitly set, context size is ignored.
   */
  private _initContainerContext(): void {
    if (this._contextService?.getType() !== PHOENIX_CONTEXTS.CONTAINER_COMPONENT) {
      return;
    }
    const containerService = this._contextService;
    containerService.sizeMap$.pipe(takeUntil(this._destroy$)).subscribe(sizeMap => {
      const size = containerService.getSizeForSelector(sizeMap, 'phx-radio-list') as PhxRadioListSize | null;
      if (size) {
        this.updateSize(size);
      }
    });
  }
}
